import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { trackEvent } from "@/lib/analytics";
import { generatedBlogHubEntries } from "@/data/seoContent.mjs";

const OG_IMAGE = "https://getblackride.com/logo.png";
const SEARCH_STORAGE_KEY = "blackride_blog_search_counts_v1";
const DISCUSSION_STORAGE_KEY = "blackride_blog_discussions_v1";
const DISCUSSION_LIMIT = 12;
const WHATSAPP_NUMBER = "966540423401";

const basePosts = [
  {
    id: 0,
    title: "How to Book a Chauffeur Service in Saudi Arabia",
    date: "May 11, 2026",
    excerpt:
      "A practical booking guide for airport transfer, executive rides, Umrah transport, and private family travel across Saudi Arabia.",
    slug: "/blog/how-to-book-a-chauffeur-service-in-saudi-arabia",
    topics: ["chauffeur service", "booking guide", "saudi arabia", "airport transfer", "family travel"],
  },
  {
    id: 0.5,
    title: "Best Way to Travel from Jeddah Airport to Makkah",
    date: "May 11, 2026",
    excerpt:
      "A premium traveler's guide to choosing the best route, vehicle class, and transfer style from Jeddah Airport to Makkah.",
    slug: "/blog/best-way-to-travel-from-jeddah-airport-to-makkah",
    topics: ["jeddah airport", "makkah", "airport transfer", "umrah", "arrival planning"],
  },
  {
    id: 0.75,
    title: "Private Transfer vs Taxi for Umrah Pilgrims",
    date: "May 11, 2026",
    excerpt:
      "Compare private transfer and taxi options for pilgrims, families, luggage-heavy arrivals, and airport-to-Makkah travel.",
    slug: "/blog/private-transfer-vs-taxi-for-umrah-pilgrims",
    topics: ["umrah", "taxi", "private transfer", "pilgrims", "jeddah airport"],
  },
  {
    id: 0.9,
    title: "Jeddah to Makkah Private Car: Complete Guide",
    date: "May 11, 2026",
    excerpt:
      "Everything travelers should know before booking a private car from Jeddah to Makkah for family, premium, or Umrah travel.",
    slug: "/blog/jeddah-to-makkah-private-car-complete-guide",
    topics: ["jeddah to makkah", "private car", "umrah transfer", "family travel", "premium transfer"],
  },
  {
    id: 0.95,
    title: "Makkah to Madinah Transfer: Best Options for Families",
    date: "May 11, 2026",
    excerpt:
      "A family-focused look at comfort, luggage, elders, and the best premium transfer choices between Makkah and Madinah.",
    slug: "/blog/makkah-to-madinah-transfer-best-options-for-families",
    topics: ["makkah to madinah", "families", "elders", "intercity transfer", "private transport"],
  },
  {
    id: 0.97,
    title: "Premium Chauffeur Service for Business Travellers in Riyadh",
    date: "May 11, 2026",
    excerpt:
      "Why executives, assistants, and hosted guests in Riyadh often prefer a premium chauffeur standard over ordinary rides.",
    slug: "/blog/premium-chauffeur-service-for-business-travellers-in-riyadh",
    topics: ["riyadh", "business travel", "executive transport", "chauffeur service", "corporate travel"],
  },
  {
    id: 0.98,
    title: "Airport Transfer in Jeddah: What Travellers Should Know",
    date: "May 11, 2026",
    excerpt:
      "A practical arrival guide covering luggage, class selection, airport pickup quality, and onward transfer planning in Jeddah.",
    slug: "/blog/airport-transfer-in-jeddah-what-travellers-should-know",
    topics: ["airport transfer", "jeddah", "arrival guide", "pickup", "saudi travel"],
  },
  {
    id: 0.99,
    title: "Safe Transport Options for Umrah Pilgrims",
    date: "May 11, 2026",
    excerpt:
      "A guide to safer, calmer premium transport options for pilgrims, elders, and family groups in Saudi Arabia.",
    slug: "/blog/safe-transport-options-for-umrah-pilgrims",
    topics: ["safe transport", "umrah", "pilgrims", "family transport", "saudi arabia"],
  },
  {
    id: 0.991,
    title: "Why GCC Families Prefer Private Chauffeur Services in Saudi Arabia",
    date: "May 11, 2026",
    excerpt:
      "See why private chauffeur travel often works better for GCC family airport arrivals, Umrah journeys, and intercity movement.",
    slug: "/blog/why-gcc-families-prefer-private-chauffeur-services-in-saudi-arabia",
    topics: ["gcc families", "private chauffeur", "saudi arabia", "family transport", "premium service"],
  },
  {
    id: 0.992,
    title: "Luxury Car with Driver in Saudi Arabia: Complete Booking Guide",
    date: "May 11, 2026",
    excerpt:
      "A premium travel guide to booking the right luxury car with driver for airport, executive, family, and intercity journeys.",
    slug: "/blog/luxury-car-with-driver-in-saudi-arabia-complete-booking-guide",
    topics: ["luxury car", "driver", "saudi arabia", "booking guide", "executive travel"],
  },
  {
    id: 1,
    title: "Benefits of Hiring a Chauffeur Service in Jeddah",
    date: "April 21, 2026",
    excerpt:
      "Discover why a chauffeur service in Jeddah is a smarter choice for airport transfers, business trips, family travel, and premium city transportation.",
    slug: "/blog/jeddah-chauffeur-service-benefits",
    topics: ["jeddah", "chauffeur service", "airport transfer", "business trips", "family travel"],
  },
  {
    id: 2,
    title: "Jeddah Airport Transfer Guide",
    date: "April 21, 2026",
    excerpt: "A practical guide to booking airport transfers in Jeddah, choosing the right vehicle, and avoiding travel stress after landing.",
    slug: "/blog/jeddah-airport-transfer-guide",
    topics: ["jeddah airport", "airport transfer", "vehicle choice", "travel guide", "landing"],
  },
  {
    id: 3,
    title: "Uber vs Private Chauffeur in Saudi Arabia",
    date: "April 21, 2026",
    excerpt: "Compare Uber and private chauffeur services in Saudi Arabia for reliability, comfort, business travel, and airport transfers.",
    slug: "/blog/uber-vs-private-chauffeur-saudi-arabia",
    topics: ["uber", "chauffeur", "comparison", "saudi arabia", "reliability"],
  },
  {
    id: 4,
    title: "Why Business Travelers Choose Chauffeur Service in Riyadh",
    date: "April 21, 2026",
    excerpt: "See why executives and corporate travelers in Riyadh often prefer chauffeur services for punctuality, privacy, and a more professional travel experience.",
    slug: "/blog/riyadh-business-travel-chauffeur-service",
    topics: ["riyadh", "business travelers", "chauffeur service", "executives", "privacy"],
  },
  {
    id: 5,
    title: "Benefits of a Private Transfer from Jeddah to Makkah",
    date: "April 21, 2026",
    excerpt: "Learn why private transfers from Jeddah to Makkah can be more comfortable, predictable, and convenient for families and religious travelers.",
    slug: "/blog/jeddah-to-makkah-private-transfer-benefits",
    topics: ["jeddah to makkah", "private transfer", "families", "religious travel", "comfort"],
  },
  {
    id: 0.11,
    title: "UAE to Saudi Umrah Transport: What Families Should Plan Before Arrival",
    date: "May 11, 2026",
    excerpt:
      "A GCC-focused guide for travelers coming from the UAE who need premium Umrah transport arrangements inside Saudi Arabia after arrival.",
    slug: "/blog/uae-to-saudi-umrah-transport",
    topics: ["uae", "umrah transport", "dubai", "abu dhabi", "gcc families"],
  },
  {
    id: 0.12,
    title: "Qatar to Saudi Umrah Transport: How Doha Families Should Plan Ground Travel",
    date: "May 11, 2026",
    excerpt:
      "A practical guide for families and pilgrims traveling from Qatar who want premium Saudi-side Umrah transport after arrival.",
    slug: "/blog/qatar-to-saudi-umrah-transport",
    topics: ["qatar", "doha", "umrah transport", "gcc families", "saudi transfers"],
  },
  {
    id: 0.13,
    title: "Kuwait to Saudi Umrah Transport: A Premium Planning Guide for Families",
    date: "May 11, 2026",
    excerpt:
      "Learn how Kuwait-based Umrah travelers can plan premium Saudi ground transport for airport arrivals and intercity travel.",
    slug: "/blog/kuwait-to-saudi-umrah-transport",
    topics: ["kuwait", "umrah transport", "family travel", "saudi arabia", "airport transfer"],
  },
  {
    id: 0.14,
    title: "Bahrain to Saudi Umrah Transport: Premium Ground Travel for Pilgrims and Families",
    date: "May 11, 2026",
    excerpt:
      "A practical guide for Bahrain-based pilgrims who want premium private transport inside Saudi Arabia for Umrah-related travel.",
    slug: "/blog/bahrain-to-saudi-umrah-transport",
    topics: ["bahrain", "umrah transport", "pilgrims", "family travel", "premium transfer"],
  },
  {
    id: 0.15,
    title: "Oman to Saudi Umrah Transport: Planning Premium Transfers for a Smoother Arrival",
    date: "May 11, 2026",
    excerpt:
      "A guide for Oman-based pilgrims who want premium private transport inside Saudi Arabia for Umrah and family travel.",
    slug: "/blog/oman-to-saudi-umrah-transport",
    topics: ["oman", "umrah transport", "airport arrival", "makkah", "madinah"],
  },
  {
    id: 0.16,
    title: "Travelling from UAE for Umrah? Book Private Transport in Saudi Arabia",
    date: "May 11, 2026",
    excerpt:
      "A conversion-focused guide for UAE travelers who want premium private chauffeur transport in Saudi Arabia for Umrah arrivals and family movements.",
    slug: "/blog/travelling-from-uae-for-umrah-book-private-transport-in-saudi-arabia",
    topics: ["uae umrah", "private transport", "dubai", "abu dhabi", "chauffeur service"],
  },
  {
    id: 0.17,
    title: "Best Chauffeur Service for GCC Families Visiting Makkah",
    date: "May 11, 2026",
    excerpt:
      "A premium family travel guide for GCC visitors who want the right chauffeur service standard for Makkah arrivals and Umrah travel.",
    slug: "/blog/best-chauffeur-service-for-gcc-families-visiting-makkah",
    topics: ["gcc families", "makkah", "chauffeur service", "family transport", "private driver"],
  },
  {
    id: 0.18,
    title: "Private Umrah Transport for Families from Dubai, Abu Dhabi, Doha & Kuwait",
    date: "May 11, 2026",
    excerpt:
      "A GCC-focused guide to private Umrah transport for families arriving from Dubai, Abu Dhabi, Doha, and Kuwait.",
    slug: "/blog/private-umrah-transport-for-families-from-dubai-abu-dhabi-doha-kuwait",
    topics: ["gcc", "umrah families", "dubai", "doha", "kuwait"],
  },
];

const posts = [...basePosts, ...generatedBlogHubEntries].filter(
  (post, index, collection) => collection.findIndex((item) => item.slug === post.slug) === index,
);

const resources = [
  {
    title: "Chauffeur Service Saudi Arabia",
    href: "/chauffeur-service-saudi-arabia",
    description: "Nationwide premium chauffeur page for airport transfer, intercity travel, and private city movement.",
  },
  {
    title: "Jeddah Airport to Makkah Transfer",
    href: "/jeddah-airport-to-makkah-transfer",
    description: "One of the strongest commercial pages for pilgrims and international arrivals continuing to Makkah.",
  },
  {
    title: "Makkah to Madinah Transfer",
    href: "/makkah-to-madinah-transfer",
    description: "Intercity route page for families, Umrah travelers, and premium private road journeys.",
  },
  {
    title: "Corporate Chauffeur Service Saudi Arabia",
    href: "/corporate-chauffeur-service-saudi-arabia",
    description: "Executive and hosted-guest transport page for business travel across Saudi Arabia.",
  },
  {
    title: "Premium Family Transport Saudi Arabia",
    href: "/premium-family-transport-saudi-arabia",
    description: "Best-fit page for families, elders, children, and luggage-heavy private travel.",
  },
];

const starterThreads = [
  {
    id: "airport-pickup",
    title: "Best pickup plan after landing in Jeddah Airport?",
    category: "Airport Transfers",
    snippet: "Guests often ask how much time to allow for immigration, baggage, and a smooth curbside pickup.",
    href: "/blog/airport-transfer-in-jeddah-what-travellers-should-know",
  },
  {
    id: "umrah-transfer",
    title: "Private transfer or taxi for Umrah arrivals?",
    category: "Umrah Travel",
    snippet: "Pilgrims usually compare predictability, luggage support, and comfort between taxi and chauffeur-led transfers.",
    href: "/blog/private-transfer-vs-taxi-for-umrah-pilgrims",
  },
  {
    id: "family-intercity",
    title: "Which route setup works best for families between Makkah and Madinah?",
    category: "Family Transport",
    snippet: "Large families usually want the right vehicle class, rest timing, and a calmer long-distance road experience.",
    href: "/blog/makkah-to-madinah-transfer-best-options-for-families",
  },
];

function ensureMeta(selector, identity, content) {
  let node = document.querySelector(selector);
  if (!node) {
    node = document.createElement("meta");
    Object.entries(identity).forEach(([key, value]) => node.setAttribute(key, value));
    document.head.appendChild(node);
  }
  node.setAttribute("content", content);
}

function normalize(value) {
  return String(value || "").toLowerCase().trim();
}

function readSearchCounts() {
  if (typeof window === "undefined") return {};
  try {
    return JSON.parse(window.localStorage.getItem(SEARCH_STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

function readDiscussions() {
  if (typeof window === "undefined") return [];
  try {
    const stored = JSON.parse(window.localStorage.getItem(DISCUSSION_STORAGE_KEY) || "[]");
    return Array.isArray(stored) ? stored : [];
  } catch {
    return [];
  }
}

function buildWhatsappLink(topic, question) {
  const text = encodeURIComponent(
    `Hello BlackRide, I have a blog/discussion question. Topic: ${topic || "General"}. Question: ${question || "I need help choosing the right service."}`,
  );
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${text}`;
}

export default function Blog() {
  const { t, language } = useLanguage();
  const isArabic = language === "ar";
  const [searchInput, setSearchInput] = useState("");
  const [activeQuery, setActiveQuery] = useState("");
  const [searchCounts, setSearchCounts] = useState({});
  const [discussions, setDiscussions] = useState([]);
  const [discussionForm, setDiscussionForm] = useState({
    name: "",
    topic: "Airport Transfers",
    question: "",
  });

  useEffect(() => {
    const title = "BlackRide Blog | Chauffeur Service Insights in Saudi Arabia";
    const description =
      "Read BlackRide guides on airport transfers, chauffeur service in Jeddah, business travel, and premium transport in Saudi Arabia.";

    document.title = title;
    ensureMeta('meta[name="description"]', { name: "description" }, description);
    ensureMeta('meta[property="og:title"]', { property: "og:title" }, title);
    ensureMeta('meta[property="og:description"]', { property: "og:description" }, description);
    ensureMeta('meta[property="og:url"]', { property: "og:url" }, "https://getblackride.com/blog");
    ensureMeta('meta[property="og:type"]', { property: "og:type" }, "website");
    ensureMeta('meta[property="og:image"]', { property: "og:image" }, OG_IMAGE);
    ensureMeta('meta[name="twitter:card"]', { name: "twitter:card" }, "summary_large_image");
    ensureMeta('meta[name="twitter:title"]', { name: "twitter:title" }, title);
    ensureMeta('meta[name="twitter:description"]', { name: "twitter:description" }, description);
    ensureMeta('meta[name="twitter:image"]', { name: "twitter:image" }, OG_IMAGE);

    const canonicalTag = document.querySelector('link[rel="canonical"]');
    if (canonicalTag) {
      canonicalTag.setAttribute("href", "https://getblackride.com/blog");
    }

    setSearchCounts(readSearchCounts());
    setDiscussions(readDiscussions());
  }, []);

  const topicIndex = useMemo(() => {
    return Array.from(new Set(posts.flatMap((post) => post.topics))).sort((a, b) => a.localeCompare(b));
  }, []);

  const filteredPosts = useMemo(() => {
    if (!activeQuery) return posts;
    const query = normalize(activeQuery);
    return posts.filter((post) => {
      const haystack = [post.title, post.excerpt, post.slug, ...(post.topics || [])]
        .map(normalize)
        .join(" ");
      return haystack.includes(query);
    });
  }, [activeQuery]);

  const popularSearches = useMemo(() => {
    return Object.entries(searchCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([label, count]) => ({ label, count }));
  }, [searchCounts]);

  const recordSearch = (rawValue) => {
    const query = normalize(rawValue);
    if (!query) return;

    const previousCounts = readSearchCounts();
    const nextCounts = { ...previousCounts, [query]: (previousCounts[query] || 0) + 1 };
    if (typeof window !== "undefined") {
      window.localStorage.setItem(SEARCH_STORAGE_KEY, JSON.stringify(nextCounts));
    }
    setSearchCounts(nextCounts);

    const resultCount = posts.filter((post) => {
      const haystack = [post.title, post.excerpt, post.slug, ...(post.topics || [])]
        .map(normalize)
        .join(" ");
      return haystack.includes(query);
    }).length;

    trackEvent("blog_topic_search", {
      query,
      result_count: resultCount,
      language: language || "en",
    });

    if (resultCount === 0) {
      trackEvent("blog_topic_search_no_results", {
        query,
        language: language || "en",
      });
    }
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const query = searchInput.trim();
    const normalizedQuery = normalize(query);
    const normalizedActiveQuery = normalize(activeQuery);

    setActiveQuery(query);

    if (!normalizedQuery || normalizedQuery === normalizedActiveQuery) {
      return;
    }

    recordSearch(query);
  };

  const handleTopicClick = (topic) => {
    const normalizedTopic = normalize(topic);
    const normalizedActiveQuery = normalize(activeQuery);

    setSearchInput(topic);
    setActiveQuery(topic);

    if (!normalizedTopic || normalizedTopic === normalizedActiveQuery) {
      return;
    }

    recordSearch(topic);
  };

  const handleDiscussionSubmit = (event) => {
    event.preventDefault();
    const name = discussionForm.name.trim();
    const question = discussionForm.question.trim();
    if (!name || !question) return;

    const nextEntry = {
      id: `${Date.now()}`,
      name,
      topic: discussionForm.topic,
      question,
      createdAt: new Date().toISOString(),
    };

    const nextDiscussions = [nextEntry, ...readDiscussions()].slice(0, DISCUSSION_LIMIT);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(DISCUSSION_STORAGE_KEY, JSON.stringify(nextDiscussions));
    }
    setDiscussions(nextDiscussions);
    setDiscussionForm({ name: "", topic: discussionForm.topic, question: "" });

    trackEvent("blog_discussion_created", {
      topic: nextEntry.topic,
      language: language || "en",
    });
  };

  return (
    <div dir={isArabic ? "rtl" : "ltr"} className="min-h-screen bg-black text-white px-6 py-16 md:px-12 pt-24 md:pt-28">
      <div className="max-w-6xl mx-auto">
        <div className="mb-12 max-w-4xl">
          <p className="text-xs uppercase tracking-[0.25em] text-[#C7A239] font-medium mb-4">
            {isArabic ? "أحدث المقالات" : "Insights & Updates"}
          </p>
          <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl font-semibold mb-4">
            {t("blogTitle")}
          </h1>
          <p className="text-gray-300 max-w-3xl leading-8 mb-6">{t("blogSubtitle")}</p>
          <p className="text-gray-400 max-w-3xl leading-8">
            {isArabic
              ? "توفر هذه الصفحة أدلة ومقالات عملية للمسافرين الدوليين وضيوف الأعمال والعائلات الذين يبحثون عن خدمة سائق خاص راقية في جدة والمطار والمدن الرئيسية في السعودية."
              : "This hub brings together practical BlackRide guides for international visitors, business travelers, and premium guests who want airport transfer, chauffeur service, and private ground travel in Jeddah and across Saudi Arabia."}
          </p>
        </div>

        <section className="rounded-[28px] border border-white/10 bg-[#111] p-6 md:p-8 mb-10 shadow-[0_24px_90px_rgba(0,0,0,0.35)]">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <p className="text-xs uppercase tracking-[0.25em] text-[#C7A239] font-medium mb-3">
                {isArabic ? "ابحث حسب الموضوع" : "Search by topic"}
              </p>
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-white mb-3">
                {isArabic ? "اعثر على الدليل المناسب بسرعة" : "Find the right guide in seconds"}
              </h2>
              <p className="text-gray-400 leading-7">
                {isArabic
                  ? "ابحث عن المطار، العمرة، السفر العائلي، التنقل بين المدن، أو أي موضوع يهتم به ضيوف بلاك رايد."
                  : "Search airport transfer, Umrah travel, family transport, intercity routes, or the premium chauffeur topics your guests care about most."}
              </p>
            </div>
            <form onSubmit={handleSearchSubmit} className="w-full lg:max-w-xl">
              <div className="flex flex-col gap-3 md:flex-row">
                <input
                  type="search"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder={isArabic ? "ابحث عن موضوع مثل مطار جدة أو العمرة" : "Search topics like Jeddah Airport, Umrah, family transfer..."}
                  className="flex-1 rounded-full border border-white/10 bg-black/40 px-5 py-4 text-white placeholder:text-gray-500 focus:outline-none focus:border-[#C7A239]/60"
                />
                <button
                  type="submit"
                  className="rounded-full bg-[#C7A239] px-6 py-4 text-black font-semibold hover:bg-[#d6b24b] transition"
                >
                  {isArabic ? "بحث" : "Search topics"}
                </button>
              </div>
            </form>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            {topicIndex.slice(0, 12).map((topic) => (
              <button
                key={topic}
                type="button"
                onClick={() => handleTopicClick(topic)}
                className="rounded-full border border-white/10 bg-black/30 px-4 py-2 text-sm text-gray-300 hover:border-[#C7A239]/40 hover:text-white transition"
              >
                {topic}
              </button>
            ))}
          </div>

          {popularSearches.length > 0 && (
            <div className="mt-8 rounded-2xl border border-white/10 bg-black/20 p-5">
              <div className="flex items-center justify-between gap-4 mb-4">
                <h3 className="text-lg font-semibold text-white">
                  {isArabic ? "موضوعات يتم البحث عنها" : "Popular recent topic searches"}
                </h3>
                <span className="text-xs uppercase tracking-[0.2em] text-gray-500">
                  {isArabic ? "في هذا المتصفح" : "On this browser"}
                </span>
              </div>
              <div className="flex flex-wrap gap-3">
                {popularSearches.map((item) => (
                  <button
                    key={item.label}
                    type="button"
                    onClick={() => handleTopicClick(item.label)}
                    className="rounded-full border border-[#C7A239]/20 bg-[#C7A239]/10 px-4 py-2 text-sm text-[#f3db93] hover:bg-[#C7A239]/15 transition"
                  >
                    {item.label} <span className="text-white/60">({item.count})</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </section>

        {activeQuery && (
          <section className="mb-10 rounded-2xl border border-white/10 bg-[#111] p-6 md:p-8">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-[#C7A239] font-medium mb-2">
                  {isArabic ? "نتائج البحث" : "Topic results"}
                </p>
                <h2 className="text-2xl font-semibold text-white">
                  {isArabic ? `نتائج البحث عن: ${activeQuery}` : `Results for: ${activeQuery}`}
                </h2>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSearchInput("");
                  setActiveQuery("");
                }}
                className="rounded-full border border-white/10 px-4 py-2 text-sm text-gray-300 hover:border-white/20 hover:text-white transition"
              >
                {isArabic ? "مسح البحث" : "Clear search"}
              </button>
            </div>

            {filteredPosts.length > 0 ? (
              <div className="mt-6 grid gap-4">
                {filteredPosts.map((post) => (
                  <article key={post.id} className="rounded-2xl border border-white/10 bg-black/20 p-5 hover:border-[#C7A239]/35 transition">
                    <p className="text-sm text-[#C7A239] mb-2">{post.date}</p>
                    <h3 className="text-xl font-semibold mb-2">
                      <Link to={post.slug} className="hover:text-[#C7A239] transition">
                        {post.title}
                      </Link>
                    </h3>
                    <p className="text-gray-400 leading-7 mb-4">{post.excerpt}</p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {post.topics.slice(0, 4).map((topic) => (
                        <button
                          key={topic}
                          type="button"
                          onClick={() => handleTopicClick(topic)}
                          className="rounded-full border border-white/10 px-3 py-1 text-xs text-gray-300 hover:border-[#C7A239]/40 transition"
                        >
                          {topic}
                        </button>
                      ))}
                    </div>
                    <Link to={post.slug} className="text-[#C7A239] font-medium hover:underline">
                      {isArabic ? "اقرأ المقال" : "Read article"}
                    </Link>
                  </article>
                ))}
              </div>
            ) : (
              <div className="mt-6 rounded-2xl border border-[#C7A239]/20 bg-[#C7A239]/[0.06] p-6">
                <h3 className="text-xl font-semibold text-white mb-3">
                  {isArabic ? "لم نجد موضوعا مطابقا بعد" : "We do not have a direct topic match yet"}
                </h3>
                <p className="text-gray-300 leading-7 mb-4">
                  {isArabic
                    ? "تم حفظ هذا البحث ليفهم الفريق بشكل افضل ما يبحث عنه الزوار. يمكنك متابعة الصفحات الاقرب لموضوعك او مراسلة بلاك رايد مباشرة على واتساب."
                    : "We are tracking this search so we can understand what travelers are asking about. You can continue with the closest guides below or message BlackRide directly on WhatsApp."}
                </p>
                <div className="flex flex-wrap gap-3 mb-5">
                  {topicIndex.slice(0, 8).map((topic) => (
                    <button
                      key={topic}
                      type="button"
                      onClick={() => handleTopicClick(topic)}
                      className="rounded-full border border-white/10 px-4 py-2 text-sm text-gray-200 hover:border-[#C7A239]/40 transition"
                    >
                      {topic}
                    </button>
                  ))}
                </div>
                <a
                  href={buildWhatsappLink(activeQuery, `I searched for "${activeQuery}" and need help finding the right BlackRide service.`)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex rounded-full bg-[#C7A239] px-5 py-3 text-black font-semibold hover:bg-[#d6b24b] transition"
                >
                  {isArabic ? "اسال بلاك رايد على واتساب" : "Ask BlackRide on WhatsApp"}
                </a>
              </div>
            )}
          </section>
        )}

        <div className="grid gap-6 lg:grid-cols-[1.4fr_0.8fr] items-start mb-12">
          <div className="grid gap-6">
            {(activeQuery ? filteredPosts : posts).map((post) => (
              <article
                key={post.id}
                className="border border-white/10 rounded-2xl p-6 bg-[#111] hover:border-[#C7A239]/35 transition"
              >
                <p className="text-sm text-[#C7A239] mb-2">{post.date}</p>
                <h2 className="text-2xl font-semibold mb-3">
                  <Link to={post.slug} className="hover:text-[#C7A239] transition">
                    {post.title}
                  </Link>
                </h2>
                <p className="text-gray-400 leading-relaxed mb-4">{post.excerpt}</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {post.topics.slice(0, 4).map((topic) => (
                    <button
                      key={topic}
                      type="button"
                      onClick={() => handleTopicClick(topic)}
                      className="rounded-full border border-white/10 px-3 py-1 text-xs text-gray-300 hover:border-[#C7A239]/40 transition"
                    >
                      {topic}
                    </button>
                  ))}
                </div>
                <Link to={post.slug} className="text-[#C7A239] font-medium hover:underline">
                  {isArabic ? "اقرا المقال" : "Read article"}
                </Link>
              </article>
            ))}
          </div>

          <aside className="space-y-6">
            <div className="rounded-2xl border border-white/10 bg-[#111] p-6">
              <h2 className="text-2xl font-semibold text-white mb-4">
                {isArabic ? "صفحات موصى بها" : "Recommended pages"}
              </h2>
              <p className="text-gray-400 leading-7 mb-6">
                {isArabic
                  ? "اربط بين الادلة والمحتوى التجاري للحصول على تجربة اوضح للمستخدم ومحركات البحث."
                  : "Explore the commercial pages that match our most searched travel intents, from airport transfer to premium chauffeur service in Jeddah."}
              </p>
              <div className="space-y-4">
                {resources.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className="block rounded-2xl border border-white/10 bg-black/30 p-4 hover:border-[#C7A239]/40 transition"
                  >
                    <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                    <p className="text-sm text-gray-400 leading-6">{item.description}</p>
                  </Link>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-[#111] p-6">
              <p className="text-xs uppercase tracking-[0.25em] text-[#C7A239] font-medium mb-3">
                {isArabic ? "مواضيع يستكشفها الزوار" : "Topics travelers often explore"}
              </p>
              <div className="flex flex-wrap gap-3">
                {topicIndex.slice(0, 16).map((topic) => (
                  <button
                    key={topic}
                    type="button"
                    onClick={() => handleTopicClick(topic)}
                    className="rounded-full border border-white/10 bg-black/20 px-4 py-2 text-sm text-gray-300 hover:border-[#C7A239]/40 hover:text-white transition"
                  >
                    {topic}
                  </button>
                ))}
              </div>
            </div>
          </aside>
        </div>

        <section className="rounded-[28px] border border-white/10 bg-[#111] p-8 md:p-10 mb-10">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-8">
            <div className="max-w-3xl">
              <p className="text-xs uppercase tracking-[0.25em] text-[#C7A239] font-medium mb-3">
                {isArabic ? "منتدى النقاش" : "Discussion forum"}
              </p>
              <h2 className="text-3xl md:text-4xl font-['Playfair_Display'] text-white mb-3">
                {isArabic ? "ناقش موضوعات السفر التي تهمك" : "Discuss the travel topics that matter to you"}
              </h2>
              <p className="text-gray-400 leading-7">
                {isArabic
                  ? "ابدا سؤالا جديدا، راجع موضوعات شائعة، او تابع مباشرة مع فريق بلاك رايد على واتساب اذا كانت رحلتك عاجلة."
                  : "Start a question, browse the most common BlackRide discussion prompts, or continue directly on WhatsApp when your travel need is time-sensitive."}
              </p>
            </div>
            <a
              href={buildWhatsappLink("Discussion forum", "I need help choosing the right BlackRide service for my trip.")}
              target="_blank"
              rel="noreferrer"
              className="inline-flex rounded-full border border-[#C7A239]/30 bg-[#C7A239]/10 px-5 py-3 text-[#f3db93] font-medium hover:bg-[#C7A239]/15 transition"
            >
              {isArabic ? "ابدأ النقاش على واتساب" : "Continue discussion on WhatsApp"}
            </a>
          </div>

          <div className="grid gap-6 lg:grid-cols-[1fr_0.95fr]">
            <div className="space-y-4">
              {starterThreads.map((thread) => (
                <div key={thread.id} className="rounded-2xl border border-white/10 bg-black/20 p-5">
                  <div className="flex items-center justify-between gap-4 mb-3">
                    <span className="rounded-full border border-white/10 px-3 py-1 text-xs uppercase tracking-[0.2em] text-[#C7A239]">
                      {thread.category}
                    </span>
                    <Link to={thread.href} className="text-sm text-[#C7A239] hover:underline">
                      {isArabic ? "عرض الدليل" : "View guide"}
                    </Link>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{thread.title}</h3>
                  <p className="text-gray-400 leading-7">{thread.snippet}</p>
                </div>
              ))}

              {discussions.length > 0 && (
                <div className="rounded-2xl border border-white/10 bg-black/20 p-5">
                  <h3 className="text-xl font-semibold text-white mb-4">
                    {isArabic ? "اسئلة بداها الزوار" : "Recent visitor discussions"}
                  </h3>
                  <div className="space-y-4">
                    {discussions.map((item) => (
                      <div key={item.id} className="rounded-2xl border border-white/10 px-4 py-4">
                        <div className="flex items-center justify-between gap-4 mb-2">
                          <p className="text-white font-medium">{item.name}</p>
                          <span className="text-xs uppercase tracking-[0.18em] text-[#C7A239]">{item.topic}</span>
                        </div>
                        <p className="text-gray-400 leading-7">{item.question}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <form onSubmit={handleDiscussionSubmit} className="rounded-2xl border border-white/10 bg-black/25 p-6">
              <h3 className="text-2xl font-semibold text-white mb-2">
                {isArabic ? "ابدا مناقشة جديدة" : "Start a discussion"}
              </h3>
              <p className="text-gray-400 leading-7 mb-6">
                {isArabic
                  ? "اضف موضوعا او سؤالا لنعرف ما يهتم به الزوار بشكل اكبر."
                  : "Share a topic or question so we can understand what travelers are trying to solve before booking."}
              </p>

              <div className="grid gap-4 md:grid-cols-2 mb-4">
                <input
                  type="text"
                  value={discussionForm.name}
                  onChange={(event) => setDiscussionForm((prev) => ({ ...prev, name: event.target.value }))}
                  placeholder={isArabic ? "الاسم" : "Your name"}
                  className="rounded-2xl border border-white/10 bg-[#111] px-4 py-3 text-white placeholder:text-gray-500 focus:outline-none focus:border-[#C7A239]/60"
                />
                <select
                  value={discussionForm.topic}
                  onChange={(event) => setDiscussionForm((prev) => ({ ...prev, topic: event.target.value }))}
                  className="rounded-2xl border border-white/10 bg-[#111] px-4 py-3 text-white focus:outline-none focus:border-[#C7A239]/60"
                >
                  <option>Airport Transfers</option>
                  <option>Umrah Travel</option>
                  <option>Family Transport</option>
                  <option>Executive Travel</option>
                  <option>Vehicle Classes</option>
                  <option>Intercity Routes</option>
                </select>
              </div>

              <textarea
                value={discussionForm.question}
                onChange={(event) => setDiscussionForm((prev) => ({ ...prev, question: event.target.value }))}
                placeholder={isArabic ? "ما السؤال او الموضوع الذي تبحث عنه؟" : "What topic or question are you trying to solve?"}
                rows={6}
                className="w-full rounded-2xl border border-white/10 bg-[#111] px-4 py-3 text-white placeholder:text-gray-500 focus:outline-none focus:border-[#C7A239]/60 mb-4"
              />

              <div className="flex flex-col gap-3 md:flex-row">
                <button
                  type="submit"
                  className="rounded-full bg-[#C7A239] px-6 py-3 text-black font-semibold hover:bg-[#d6b24b] transition"
                >
                  {isArabic ? "نشر المناقشة" : "Post discussion"}
                </button>
                <a
                  href={buildWhatsappLink(discussionForm.topic, discussionForm.question)}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-full border border-white/10 px-6 py-3 text-white font-medium hover:border-[#C7A239]/40 transition text-center"
                >
                  {isArabic ? "ارسلها الى واتساب" : "Send it to WhatsApp"}
                </a>
              </div>
            </form>
          </div>
        </section>

        <section className="rounded-2xl border border-white/10 bg-[#111] p-8 md:p-10 mb-10">
          <h2 className="text-3xl font-semibold text-white mb-4">
            {isArabic ? "كيف تستفيد من محتوى بلاك رايد" : "How to use the BlackRide guide hub"}
          </h2>
          <div className="grid gap-6 md:grid-cols-3 text-gray-300 leading-7">
            <div>
              <p className="text-[#C7A239] font-medium mb-2">01</p>
              <h3 className="text-xl text-white font-semibold mb-2">
                {isArabic ? "ابدا بالمقال الاقرب لرحلتك" : "Start with the guide closest to your route"}
              </h3>
              <p>
                {isArabic
                  ? "اذا كانت رحلتك من مطار جدة الى مكة او من مكة الى المدينة، ابدا بالمحتوى المرتبط مباشرة بالرحلة."
                  : "If your trip involves Jeddah Airport, Makkah, Madinah, Riyadh, or family transport, open the most route-specific article first."}
              </p>
            </div>
            <div>
              <p className="text-[#C7A239] font-medium mb-2">02</p>
              <h3 className="text-xl text-white font-semibold mb-2">
                {isArabic ? "انتقل الى الصفحة التجارية المناسبة" : "Move from the guide to the matching service page"}
              </h3>
              <p>
                {isArabic
                  ? "كل مقال يرتبط بصفحات خدمة مناسبة حتى يتمكن الزائر من الانتقال من الاستكشاف الى التواصل مع بلاك رايد."
                  : "Each guide is connected to relevant service pages so visitors can move from research into a direct booking conversation with BlackRide."}
              </p>
            </div>
            <div>
              <p className="text-[#C7A239] font-medium mb-2">03</p>
              <h3 className="text-xl text-white font-semibold mb-2">
                {isArabic ? "استخدم واتساب للحصول على تاكيد سريع" : "Use WhatsApp for quick confirmation"}
              </h3>
              <p>
                {isArabic
                  ? "بعد معرفة المسار او الفئة المناسبة، تواصل مع الفريق مباشرة عبر واتساب لتاكيد التوفر والتسعير والتفاصيل."
                  : "Once you know the likely route or vehicle class, continue to WhatsApp so the operations team can confirm availability, pricing, and trip timing."}
              </p>
            </div>
          </div>
        </section>

        <div className="mt-12">
          <Link
            to="/"
            className="inline-block border border-[#C7A239] text-[#C7A239] px-6 py-3 rounded-full hover:bg-[#C7A239] hover:text-black transition"
          >
            {isArabic ? "العودة الى الرئيسية" : "Back to homepage"}
          </Link>
        </div>
      </div>
    </div>
  );
}
